<template>
    <Card v-if="card" ref="chartContainer" class="chart-container" overlay :loading="loading || updating">
        <ErrorMessage :value="error" />
        <ECharts ref="chart" v-bind="attrs" :init-options="initOptions" v-on="listeners" />
        <template #header>
            <div v-if="header" class="d-flex gap-2">
                <span>{{ header }}</span>
                <ButtonIcon
                    icon="download"
                    variant="light"
                    :icon-size="14"
                    class="p-1 rounded-max mb-auto width-25 height-25"
                    :title="$t('export')"
                    @click="exportChart"
                />
                <ButtonIcon
                    icon="maximize"
                    variant="light"
                    :icon-size="14"
                    class="p-1 rounded-max mb-auto width-25 height-25"
                    :title="$t('fullscreen')"
                    @click="fullscreenChart"
                />
            </div>
        </template>
        <template #nav><slot name="nav"></slot></template>
    </Card>
    <b-overlay v-else :show="updating" rounded="sm">
        <ECharts ref="chart" v-bind="attrs" :init-options="initOptions" v-on="listeners" />
    </b-overlay>
</template>

<script>
    import mergeWith from 'lodash/mergeWith';
    import ECharts from 'vue-echarts';
    import * as echarts from 'echarts';
    import enLocale from 'echarts/i18n/langEN-obj';
    import plLocale from 'echarts/i18n/langPL-obj';
    import { getEffectiveLocale } from '@/i18n/i18n';

    echarts.registerTransform({
        type: 'echarts:percent',
        transform(params) {
            const upstream = params.upstream;
            const { dimension } = params.config || {};
            if (!dimension || !Array.isArray(dimension)) {
                return { data: upstream.getRawData() };
            }
            const data = [];
            for (let i = 0; i < upstream.count(); i++) {
                const rawItem = upstream.getRawDataItem(i);
                let sum = 0;
                dimension.forEach(dimName => {
                    const dimInfo = upstream.getDimensionInfo(dimName);
                    if (!dimInfo) return;
                    const val = upstream.retrieveValueFromItem(rawItem, dimInfo.index);
                    sum += val;
                });
                // Dołączamy surowe dane do nowego obiektu jako __raw
                const newItem = { ...rawItem, __raw: rawItem };
                if (sum !== 0) {
                    dimension.forEach(dimName => {
                        const dimInfo = upstream.getDimensionInfo(dimName);
                        const val = upstream.retrieveValueFromItem(rawItem, dimInfo.index);
                        newItem[dimName] = (val / sum) * 100;
                    });
                } else {
                    dimension.forEach(dimName => {
                        newItem[dimName] = 0;
                    });
                }
                data.push(newItem);
            }
            return { data };
        },
    });

    /**
     * Customizer dla mergeWith – jeśli źródło jest tablicą, łączymy każdy element z wartością domyślną.
     */
    const customizer = (objValue, srcValue) => {
        if (typeof objValue === 'object' && !Array.isArray(objValue) && Array.isArray(srcValue)) {
            return srcValue.map(item => mergeWith({}, objValue, item, customizer));
        }
        return undefined;
    };

    export default {
        name: 'EChart',
        inheritAttrs: false,
        components: { ECharts },
        props: {
            error: {
                type: [Error, Object],
                default: null,
            },
            header: {
                type: String,
                default: '',
            },
            card: {
                type: Boolean,
                default: false,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            width: {
                type: [String, Number],
                default: '100%',
            },
            height: {
                type: [String, Number],
                default: '400px',
            },
            type: {
                type: String,
                default: undefined,
            },
        },
        data() {
            return {
                initOptions: { locale: 'PL' },
                updating: false,
                defaultOptions: {
                    grid: {
                        left: '20px',
                        right: '20px',
                        top: '10px',
                        bottom: '10px',
                        containLabel: true,
                    },
                    tooltip: {
                        show: true,
                    },
                    xAxis: {},
                    yAxis: {
                        type: 'value',
                        nameRotate: 90,
                        nameLocation: 'middle',
                        nameGap: 38,
                    },
                    legend: {
                        show: true,
                        type: 'scroll',
                        orient: 'horizontal',
                        bottom: 0,
                        itemGap: 5,
                        pageIconSize: 10,
                        pageTextStyle: {
                            fontSize: 12,
                            color: '#333',
                        },
                    },
                    series: [],
                },
                fullscreenHeight: null,
            };
        },
        computed: {
            locale() {
                return this.$i18n.locale;
            },
            isTimeType() {
                if (this.type === 'time') return true;
                if (this.$attrs.options?.xAxis?.type === 'time') return true;
                return false;
            },
            computedWidth() {
                return typeof this.width === 'number' ? `${this.width}px` : this.width;
            },
            computedHeight() {
                let height = Number.parseInt(this.$attrs.options?.height);
                if (!height && this.computedOptions.yAxis?.type === 'category' && this.$attrs.options.series.some(item => item.type === 'bar' || item.type === 'rangeBar')) {
                    const items = this.computedOptions.yAxis.data;
                    const minHeight = 300;
                    const itemHeight = 50;
                    const labelsHeight = this.isTimeType ? 100 : 50;
                    height = items.length ? items.length * itemHeight + labelsHeight : minHeight;
                }
                if (!height) {
                    height = Number.parseInt(this.height);
                }
                return height;
            },
            computedOptions() {
                const effectiveLocale = getEffectiveLocale();

                // Bazowa struktura opcji wykresu
                const baseOptions = {
                    xAxis: {},
                    yAxis: {},
                    tooltip: {},
                };

                // Funkcja budująca opcje osi dla danych czasowych
                const buildTimeAxisOptions = () => ({
                    type: 'category',
                    axisLabel: {
                        formatter: value => this.$format.date(Number.parseInt(value), 'dd MMM yyyy'),
                        hideOverlap: true,
                        rotate: 45,
                    },
                    axisLine: {
                        show: true,
                    },
                    axisTick: {
                        show: true,
                    },
                });

                // Funkcja budująca opcje tooltipu dla danych czasowych
                const buildTimeTooltipOptions = () => ({
                    trigger: 'axis',
                    axisPointer: {
                        label: {
                            formatter: params => this.$format.date(Number.parseInt(params.value), 'dd MMM yyyy'),
                        },
                    },
                    valueFormatter: value => new Intl.NumberFormat(effectiveLocale).format(value || 0),
                });

                // Konfiguracja dla danych czasowych
                if (this.isTimeType) {
                    baseOptions.xAxis = mergeWith({}, baseOptions.xAxis, buildTimeAxisOptions(), customizer);
                    baseOptions.tooltip = mergeWith({}, baseOptions.tooltip, buildTimeTooltipOptions(), customizer);
                }

                // Wyłączenie linii osi dla wykresów typu "pie"
                if (this.$attrs.options.series.some(item => item.type === 'pie')) {
                    baseOptions.xAxis = mergeWith({}, baseOptions.xAxis, { axisLine: { show: false } }, customizer);
                    baseOptions.yAxis = mergeWith({}, baseOptions.yAxis, { axisLine: { show: false } }, customizer);
                }

                // Scalanie opcji: domyślne, obliczone oraz przekazane przez atrybuty
                const mergedOptions = mergeWith({}, this.defaultOptions, baseOptions, this.$attrs.options, customizer);

                // Ustawienie formatu etykiet dla osi Y, jeśli nie został określony formatter
                if (mergedOptions.yAxis?.type === 'value' && !mergedOptions.yAxis?.axisLabel?.formatter) {
                    mergedOptions.yAxis.axisLabel = mergeWith({}, mergedOptions.yAxis.axisLabel, { formatter: value => new Intl.NumberFormat(effectiveLocale).format(value) }, customizer);
                }

                // Dostosowanie marginesu siatki, gdy legenda jest widoczna
                if (mergedOptions.legend?.show) {
                    mergedOptions.grid = mergeWith({}, mergedOptions.grid, { bottom: '50px' }, customizer);
                }

                // Konfiguracja opcji serii
                if (Array.isArray(mergedOptions.series)) {
                    const defaultLineOptions = {
                        type: 'line',
                        smooth: true,
                        showSymbol: false,
                        areaStyle: { opacity: 0.3 },
                    };

                    const defaultPieOptions = mergeWith(
                        {},
                        defaultLineOptions,
                        {
                            emphasis: {
                                itemStyle: {
                                    shadowBlur: 10,
                                    shadowOffsetX: 0,
                                    shadowColor: 'rgba(0, 0, 0, 0.5)',
                                },
                            },
                        },
                        customizer
                    );

                    mergedOptions.series = mergedOptions.series.map(seriesItem => {
                        if (seriesItem.type === 'pie') {
                            return mergeWith({}, defaultPieOptions, seriesItem, customizer);
                        }
                        return mergeWith({}, defaultLineOptions, seriesItem, customizer);
                    });
                }

                // Dostosowanie tooltipu dla wykresów liniowych
                if (mergedOptions.series.some(item => item.type === 'line')) {
                    mergedOptions.tooltip = mergeWith({}, mergedOptions.tooltip, { trigger: 'axis', axisPointer: { type: 'line' } }, customizer);
                }

                // Dostosowanie boundaryGap dla wykresów liniowych
                if (mergedOptions.series.every(item => item.type === 'line')) {
                    mergedOptions.xAxis = mergeWith({}, mergedOptions.xAxis, { boundaryGap: false }, customizer);
                }

                // Przetwarzamy serie typu "rangeBar"
                if (mergedOptions.series.some(item => item.type === 'rangeBar')) {
                    // Tworzymy customowy tooltip
                    const tooltip = {
                        trigger: 'item',
                        formatter: params => {
                            const dataPoint = params.data;
                            const start = dataPoint.value[1];
                            const end = dataPoint.value[2];
                            const from = this.$format.date(Number.parseInt(start));
                            const to = this.$format.date(Number.parseInt(end));
                            const duration = this.$format.duration(start, end);
                            const color = params.color || '#000';
                            const seriesName = params.name || '';
                            return `
                                <div class="echarts-tooltip-rangebar">
                                <div style="margin-bottom: 5px">
                                    <strong class="series-name" style="color: ${color}">${seriesName}</strong>
                                </div>
                                <div>
                                    <span class="value start-value">${from}</span>
                                    <span class="separator">-</span>
                                    <span class="value end-value">${to}</span>
                                </div>
                                <div>
                                    <span class="value-period">
                                    <strong>${this.$t('duration')}:</strong> ${duration}
                                    </span>
                                </div>
                                </div>
                            `;
                        },
                    };
                    mergedOptions.tooltip = mergeWith({}, mergedOptions.tooltip, tooltip, customizer);

                    // Wyznaczamy listę kategorii na osi Y – nazwy produktów
                    // const rangeBarSeries = mergedOptions.series.filter(item => item.type === 'rangeBar');
                    const categories = this.getRangeBarCategories(mergedOptions.series);
                    mergedOptions.yAxis = mergeWith({}, mergedOptions.yAxis, { type: 'category', data: categories, axisLabel: { formatter: null } }, customizer);

                    // Dla każdej serii typu "rangeBar" przekształcamy ją na serię custom
                    mergedOptions.series = mergedOptions.series.map(seriesItem => {
                        if (seriesItem.type === 'rangeBar') {
                            // Założenie: seriesItem.data jest tablicą z jednym elementem, zawierającym dane danego produktu
                            const seriesDataItem = seriesItem.data[0] || {};
                            // seriesDataItem.data to tablica segmentów, każdy o strukturze { x: productName, y: [start, end] }
                            const newData = (seriesDataItem.data || []).map(dp => {
                                const [start, end] = dp.y;
                                // Ustalamy indeks kategorii na podstawie nazwy serii
                                const catIndex = categories.indexOf(seriesItem.name);
                                return { value: [catIndex, start, end, end - start], name: seriesItem.name };
                            });
                            return {
                                ...seriesItem,
                                type: 'custom',
                                renderItem: this.renderRangeBarItem,
                                encode: {
                                    x: [1, 2],
                                    y: 0,
                                },
                                data: newData,
                            };
                        }
                        return seriesItem;
                    });
                }

                if (mergedOptions.yAxis.type === 'category' && !mergedOptions.yAxis.axisLabel?.formatter) {
                    const formatter = value => {
                        const maxChars = 50;
                        if (value.length > maxChars) {
                            return `${value.substring(0, maxChars)}...`;
                        }
                        return value;
                    };
                    mergedOptions.yAxis = mergeWith({}, mergedOptions.yAxis, { axisLabel: { formatter } }, customizer);
                }

                // Dodanie komponentu graphic wyświetlającego "Brak danych", gdy nie ma żadnych serii
                if (!mergedOptions.series || mergedOptions.series.length === 0) {
                    mergedOptions.graphic = [
                        {
                            type: 'text',
                            left: 'center',
                            top: 'middle',
                            style: {
                                text: this.$t('noData'),
                                fill: '#ccc',
                                font: '30px sans-serif',
                            },
                        },
                    ];
                } else {
                    // W przeciwnym razie graphic jest pusty
                    mergedOptions.graphic = [];
                }

                delete mergedOptions.height;

                return mergedOptions;
            },
            attrs() {
                let height = this.fullscreenHeight || this.computedHeight;
                height = typeof height === 'number' ? `${height}px` : height;
                return {
                    ...this.$attrs,
                    option: this.computedOptions,
                    ref: 'chart',
                    style: {
                        width: this.computedWidth,
                        height,
                    },
                    autoresize: true,
                    manualUpdate: true,
                };
            },
            listeners() {
                return { ...this.$listeners, finished: this.eventFinished };
            },
        },
        watch: {
            locale: {
                handler() {
                    const effectiveLocale = getEffectiveLocale();
                    if (effectiveLocale === 'pl') echarts.registerLocale('PL', plLocale);
                    if (effectiveLocale === 'en-GB') echarts.registerLocale('EN', enLocale);
                },
                immediate: true,
            },
            computedOptions(newOptions, oldOptions) {
                if (JSON.stringify(newOptions || {}) !== JSON.stringify(oldOptions || {})) {
                    this.updating = true;
                    this.$nextTick(() => {
                        const chart = this.$refs.chart.chart;
                        chart.setOption(newOptions, { replaceMerge: ['graphic', 'series', 'xAxis', 'yAxis'] });
                    });
                }
            },
        },
        methods: {
            exportChart() {
                const url = this.$refs.chart.chart.getDataURL({
                    type: 'png',
                    pixelRatio: 2,
                    backgroundColor: '#fff',
                });
                const name = this.header.toLowerCase().replaceAll(' ', '-');
                this.$utils.exportData(url, name);
            },
            fullscreenChart() {
                const container = this.$refs.chartContainer.$el;
                if (!document.fullscreenElement) {
                    container.requestFullscreen({ navigationUI: 'show' });
                } else {
                    document.exitFullscreen();
                }
            },
            adjustChartHeight(e) {
                if (document.fullscreenElement && e.type === 'fullscreenchange' && e.target === this.$refs.chartContainer?.$el) {
                    setTimeout(() => {
                        const containerHeight = this.$refs.chartContainer?.$el?.offsetHeight || 0;
                        this.fullscreenHeight = Math.max(Math.min(800, containerHeight - 80), this.computedHeight);
                    }, 100);
                } else {
                    this.fullscreenHeight = null;
                }
            },
            eventFinished() {
                this.updating = false;
            },
            getRangeBarCategories(apexData) {
                // Wyznaczamy unikalne kategorie - używamy nazwy serii
                const categories = apexData.map(series => series.name);
                return categories;
            },
            // Metoda do renderowania elementów serii custom
            renderRangeBarItem(params, api) {
                const categoryIndex = api.value(0);
                const startCoord = api.coord([api.value(1), categoryIndex]);
                const endCoord = api.coord([api.value(2), categoryIndex]);
                const height = api.size([0, 1])[1] * 0.6; // 60% wysokości kategorii
                const rectShape = echarts.graphic.clipRectByRect(
                    {
                        x: startCoord[0],
                        y: startCoord[1] - height / 2,
                        width: endCoord[0] - startCoord[0],
                        height,
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    }
                );
                return (
                    rectShape && {
                        type: 'rect',
                        shape: rectShape,
                        style: api.style(),
                    }
                );
            },
        },
        mounted() {
            document.addEventListener('fullscreenchange', this.adjustChartHeight);
        },
        beforeUnmount() {
            document.removeEventListener('fullscreenchange', this.adjustChartHeight);
        },
    };
</script>
